import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CloseIcon from '@mui/icons-material/Close';
import ReactApexChart from 'react-apexcharts';
import {
  getIdleReasonEventDetail,
  getComponentReportIdleDetail,
  getProgramReportByTime,
  getProgramPartDetail,
  getMachineProgramReportGroupDetail,
  getProgramReportGroupDetail,
} from '../../../Services/app/oeeservice';
import { getStartAndEndTime } from '../utils/programTimeRange';

const BORDER = '#ECEFF3';
const TEXT_PRIMARY = '#0b0b0b';
const TEXT_MUTED = '#898781';
const STATUS_CRITICAL = '#d03b3b';
const STATUS_GOOD = '#0ca30c';

const BUCKET_COLOR = {
  low: '#eb6834',
  normal: STATUS_GOOD,
  high: '#d03b3b',
};

const BucketChip = ({ bucket }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 1,
      py: 0.25,
      borderRadius: '10px',
      fontSize: '12px',
      fontWeight: 600,
      color: '#fff',
      background: BUCKET_COLOR[bucket] || TEXT_MUTED,
      textTransform: 'capitalize',
    }}
  >
    {bucket || 'n/a'}
  </Box>
);

const StatusBox = ({ children }) => (
  <Box
    sx={{
      p: 6,
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      color: TEXT_MUTED,
      gap: 1,
    }}
  >
    {children}
  </Box>
);

const fmtTime = (val) => {
  if (!val) return '—';
  if (typeof val === 'number') return new Date(val).toLocaleString();
  if (typeof val === 'string') {
    const num = Number(val);
    if (!isNaN(num) && val.trim() !== '') {
      return new Date(num).toLocaleString();
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d.toLocaleString();
  }
  return String(val);
};

const fmtCount = (value) =>
  value === null || value === undefined || isNaN(value) ? '—' : Math.floor(Number(value));

const fmtCost = (value) =>
  value === null || value === undefined || isNaN(value) ? '—' : `₹${Math.round(Number(value)).toLocaleString('en-IN')}`;

const secondsToHms = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '—';
  const sign = totalSeconds < 0 ? '-' : '';
  const abs = Math.abs(Math.round(totalSeconds));
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = abs % 60;
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const secondsToShortDuration = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '—';
  const abs = Math.abs(Math.round(totalSeconds));
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  const seconds = abs % 60;

  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(' ');
};

function ReasonDetailModal({ open, onClose, machine, sessionStartTime, sessionEndTime, componentName, machiningTime, runTime, fromDate, toDate }) {

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const chartData = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    const parseToSeconds = (val) => {
      if (typeof val === 'number') return val;
      if (!val) return 0;
      if (typeof val === 'string' && val.includes(':')) {
        const parts = val.split(':').map(Number);
        if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
        if (parts.length === 2) return parts[0] * 60 + parts[1];
      }
      return parseFloat(val) || 0;
    };

    const aggregatedMap = {};
    data.forEach((item) => {
      const reason = (item.reason || '').trim() || 'No reason';
      const secs = parseToSeconds(item.idle_duration);
      aggregatedMap[reason] = (aggregatedMap[reason] || 0) + secs;
    });

    const sorted = Object.entries(aggregatedMap)
      .map(([reason, secs]) => ({ reason, secs }))
      .sort((a, b) => b.secs - a.secs);

    const categories = [];
    const barSeries = [];

    sorted.forEach((item) => {
      categories.push(item.reason);
      barSeries.push(item.secs);
    });

    const totalSecs = barSeries.reduce((acc, secs) => acc + secs, 0);

    let cum = 0;
    const lineSeries = barSeries.map((secs) => {
      cum += secs;
      return totalSecs > 0 ? Number(((cum / totalSecs) * 100).toFixed(1)) : 0;
    });

    return {
      categories,
      totalDuration: secondsToShortDuration(totalSecs),
      series: [
        {
          name: 'Idle Duration',
          type: 'column',
          data: barSeries,
        },
        {
          name: 'Cumulative %',
          type: 'line',
          data: lineSeries,
        },
      ],
      options: {
        chart: {
          type: 'line',
          toolbar: {
            show: true,
            tools: {
              pan: false,
            },
          },
          fontFamily: 'inherit',
        },
        colors: ['#2563eb', '#dc2626'],
        stroke: {
          width: [0, 3],
          curve: 'smooth',
        },
        plotOptions: {
          bar: {
            columnWidth: '45%',
            borderRadius: 4,
          },
        },
        xaxis: {
          categories,
          labels: {
            rotate: -45,
            style: { fontSize: '11px', fontWeight: 600 },
          },
        },
        yaxis: [
          {
            title: { text: 'Idle Duration', style: { color: '#2563eb', fontWeight: 600 } },
            labels: {
              formatter: (val) => secondsToShortDuration(val),
            },
          },
          {
            opposite: true,
            title: { text: 'Cumulative %', style: { color: '#dc2626', fontWeight: 600 } },
            min: 0,
            max: 100,
            labels: {
              formatter: (val) => `${val}%`,
            },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex }) => (seriesIndex === 0 ? secondsToShortDuration(val) : `${val}%`),
          },
        },
        legend: {
          position: 'top',
        },
      },
    };
  }, [data]);

  useEffect(() => {
    if (!open || !machine || !sessionStartTime || !sessionEndTime) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await getIdleReasonEventDetail(machine, sessionStartTime, sessionEndTime, { componentName, machiningTime, runTime, fromDate, toDate });
        if (!cancelled) setData(res || []);
      } catch (err) {
        if (!cancelled) setError('Failed to fetch idle reason details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, machine, sessionStartTime, sessionEndTime, componentName, machiningTime, runTime, fromDate, toDate]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Idle Reason Events — {machine}
          </Typography>
          {chartData && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Total Idle Duration: {chartData.totalDuration}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: '12px', color: '#555', mb: 2 }}>
          Time Range: {fmtTime(sessionStartTime)} – {fmtTime(sessionEndTime)}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ fontSize: '13px', p: 2 }}>{error}</Typography>
        ) : !chartData || chartData.categories.length === 0 ? (
          <Typography sx={{ fontSize: '13px', color: '#888', p: 2, textAlign: 'center' }}>
            No reason
          </Typography>
        ) : (
          <Box sx={{ pt: 1 }}>
            <ReactApexChart options={chartData.options} series={chartData.series} type="line" height={400} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}



// Level 3: one row per stored component_report session for one
// machine/component/cycle_time/handling_time combo, expandable into its
// own individual cycles.

function ComponentGroupRows({ machine, machineRow, filters, method, onOpenReasons, onOpenIdlePareto }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const startTime = machineRow?.start_time || filters?.startTimeMs;
        const endTime = machineRow?.end_time || filters?.endTimeMs;

        const raw = await getProgramPartDetail(machine, startTime, endTime);
        if (!cancelled) {
          const list = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.parts)
            ? raw.parts
            : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.result)
            ? raw.result
            : [];

          if (list.length > 0) {
            setRows(list);
          } else if (machineRow?.sessions && machineRow.sessions.length > 0) {
            setRows(machineRow.sessions);
          } else {
            setRows([]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch program part detail', err);
          if (machineRow?.sessions && machineRow.sessions.length > 0) {
            setRows(machineRow.sessions);
          } else {
            setError('Failed to load component breakdown.');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [machine, machineRow, filters]);

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return <Typography sx={{ p: 2, fontSize: '13px', color: STATUS_CRITICAL }}>{error}</Typography>;
  }

  if (rows.length === 0) {
    return (
      <Typography sx={{ p: 2, fontSize: '13px', color: TEXT_MUTED }}>
        No component sessions for this machine in the selected range.
      </Typography>
    );
  }

  return (
    <Table size="small" sx={{ background: '#fafafa' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 700 }}>Job / Part Name</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Start Time</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>End Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Machining Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Handling Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Target Cycle Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Run Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Idle Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Stop Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Alarm Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 160 }}>Total Additional<br />Handling Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 85 }}>Additional Run</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Actual</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Bucket</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((item, idx) => (
          <TableRow key={idx}>
            <TableCell>{item.job_name || item.component_name || '—'}</TableCell>
            <TableCell>{fmtTime(item.start_time)}</TableCell>
            <TableCell>{fmtTime(item.end_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.cycle_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.handling_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.target_cycle_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.run_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.idle_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.stop_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.alarm_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.extra_idle ?? item.additional_handling_time)}</TableCell>
            <TableCell align="right">{secondsToHms(item.extra_run ?? item.additional_run)}</TableCell>
            <TableCell align="right">{item.actual ?? '—'}</TableCell>
            <TableCell align="center">
              <BucketChip bucket={item.bucket} />
            </TableCell>
            <TableCell align="center">
              <Button
                variant="outlined"
                size="small"
                onClick={() => onOpenReasons && onOpenReasons(machine, item.start_time, item.end_time, item.job_name || item.component_name, item.cycle_time, item.run_time)}
                sx={{
                  textTransform: 'none',
                  fontSize: '11px',
                  py: 0.25,
                  px: 1,
                  whiteSpace: 'nowrap',
                  color: '#ff9800',
                  borderColor: '#ff9800',
                  '&:hover': {
                    borderColor: '#e65100',
                    backgroundColor: 'rgba(255, 152, 0, 0.08)',
                  },
                }}
              >
                Reason
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Level 4 (innermost): one row per job/program session, expandable into its
// own individual part cycles via ComponentGroupRows.
function SessionRow({ row, filters, method, onOpenReasons, onOpenIdlePareto }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.job_name || row.component_name || '—'}</TableCell>
        <TableCell>{fmtTime(row.start_time)}</TableCell>
        <TableCell>{fmtTime(row.end_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.cycle_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.handling_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.actual_setting_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.run_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.idle_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.stop_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.alarm_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_idle)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_run)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_idle1)}</TableCell>
        <TableCell align="right">{fmtCost(row.idle_cost)}</TableCell>
        <TableCell align="right">{fmtCost(row.extra_part_cost)}</TableCell>
        <TableCell align="right">{row.actual ?? '—'}</TableCell>
        <TableCell align="right">{row.low_cycle ?? 0}</TableCell>
        <TableCell align="right">{row.normal_cycle ?? 0}</TableCell>
        <TableCell align="right">{row.high_cycle ?? 0}</TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenReasons && onOpenReasons(row.machine_name, row.start_time, row.end_time, row.job_name || row.component_name, row.cycle_time, row.run_time)}
            sx={{
              textTransform: 'none',
              fontSize: '11px',
              py: 0.25,
              px: 1,
              whiteSpace: 'nowrap',
              color: '#ff9800',
              borderColor: '#ff9800',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 152, 0, 0.08)',
              },
            }}
          >
            Reason
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={21} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {expanded && (
              <ComponentGroupRows
                machine={row.machine_name}
                machineRow={row}
                filters={filters}
                method={method}
                onOpenReasons={onOpenReasons}
                onOpenIdlePareto={onOpenIdlePareto}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Level 3 wrapper: fetches program_report sessions for one machine, scoped
// to a single job_name via the program_name filter.
function SessionRows({ machine, jobRow, filters, method, onOpenReasons, onOpenIdlePareto }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const startTime = filters?.startTimeMs;
        const endTime = filters?.endTimeMs;
        const raw = await getProgramReportByTime(machine, startTime, endTime, jobRow?.job_name);
        if (!cancelled) {
          const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.result) ? raw.result : [];
          setRows(list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch job sessions', err);
          setError('Failed to load sessions.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [machine, jobRow, filters]);

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return <Typography sx={{ p: 2, fontSize: '13px', color: STATUS_CRITICAL }}>{error}</Typography>;
  }

  if (rows.length === 0) {
    return (
      <Typography sx={{ p: 2, fontSize: '13px', color: TEXT_MUTED }}>
        No sessions for this job in the selected range.
      </Typography>
    );
  }

  return (
    <Table size="small" sx={{ background: '#f4f6f9' }}>
      <TableHead>
        <TableRow>
          <TableCell />
          <TableCell sx={{ fontWeight: 700 }}>Job / Program</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>Start Time</TableCell>
          <TableCell sx={{ fontWeight: 700 }}>End Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Cycle Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Handling Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Actual Setting</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Run Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Idle Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Stop Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Alarm Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 160 }}>Total Additional<br />Handling Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 85 }}>Additional Run</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 110 }}>Extra Idle<br />(Net)</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 100 }}>Idle Cost</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 110 }}>Extra Part Cost</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Actual</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Low</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Normal</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>High</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <SessionRow
            key={row.id ?? row.start_time ?? idx}
            row={row}
            filters={filters}
            method={method}
            onOpenReasons={onOpenReasons}
            onOpenIdlePareto={onOpenIdlePareto}
          />
        ))}
      </TableBody>
    </Table>
  );
}

// Level 2: one row per job/program for a machine, summed across every
// stored program_report session for that job (job_name + cycle_time +
// handling_time), expandable into its own individual sessions.
function JobGroupRow({ row, machine, filters, method, onOpenReasons, onOpenIdlePareto }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.job_name || '—'}</TableCell>
        <TableCell align="right">{secondsToHms(row.cycle_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.handling_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.target_setting_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.actual_setting_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.run_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.idle_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.stop_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.alarm_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_idle)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_run)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_idle1)}</TableCell>
        <TableCell align="right">{fmtCost(row.idle_cost)}</TableCell>
        <TableCell align="right">{fmtCost(row.extra_part_cost)}</TableCell>
        <TableCell align="right">{fmtCount(row.extra_part_count)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_part_run_time)}</TableCell>
        <TableCell align="right">{row.actual ?? '—'}</TableCell>
        <TableCell align="right">{row.low_cycle ?? 0}</TableCell>
        <TableCell align="right">{row.normal_cycle ?? 0}</TableCell>
        <TableCell align="right">{row.high_cycle ?? 0}</TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenReasons && onOpenReasons(machine, row.start_time, row.end_time, row.job_name, row.cycle_time, row.run_time)}
            sx={{
              textTransform: 'none',
              fontSize: '11px',
              py: 0.25,
              px: 1,
              whiteSpace: 'nowrap',
              color: '#ff9800',
              borderColor: '#ff9800',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 152, 0, 0.08)',
              },
            }}
          >
            Reason
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={22} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {expanded && (
              <SessionRows
                machine={machine}
                jobRow={row}
                filters={filters}
                method={method}
                onOpenReasons={onOpenReasons}
                onOpenIdlePareto={onOpenIdlePareto}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Level 1 wrapper: fetches the per-job rollup for one machine.
function JobGroupRows({ machine, filters, method, onOpenReasons, onOpenIdlePareto }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const startTime = filters?.startTimeMs;
        const endTime = filters?.endTimeMs;
        const raw = await getProgramReportGroupDetail(machine, startTime, endTime);
        if (!cancelled) {
          const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.result) ? raw.result : [];
          setRows(list);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch program report group detail', err);
          setError('Failed to load job breakdown.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [machine, filters]);

  if (loading) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return <Typography sx={{ p: 2, fontSize: '13px', color: STATUS_CRITICAL }}>{error}</Typography>;
  }

  if (rows.length === 0) {
    return (
      <Typography sx={{ p: 2, fontSize: '13px', color: TEXT_MUTED }}>
        No jobs for this machine in the selected range.
      </Typography>
    );
  }

  return (
    <Table size="small" sx={{ background: '#fafafa' }}>
      <TableHead>
        <TableRow>
          <TableCell />
          <TableCell sx={{ fontWeight: 700 }}>Job / Program</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Cycle Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Handling Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Target Setting</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Actual Setting</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Run Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Idle Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Stop Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Total Alarm Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 160 }}>Total Additional<br />Handling Time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 85 }}>Additional Run</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 110 }}>Extra Idle<br />(Net)</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 100 }}>Idle Cost</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 110 }}>Extra Part Cost</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 110 }}>Additional<br />parts</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, minWidth: 165 }}>Hours lost to<br />excess cycle time</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Actual</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Low</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>Normal</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700 }}>High</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Reason</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, idx) => (
          <JobGroupRow
            key={row.job_name ?? idx}
            row={row}
            machine={machine}
            filters={filters}
            method={method}
            onOpenReasons={onOpenReasons}
            onOpenIdlePareto={onOpenIdlePareto}
          />
        ))}
      </TableBody>
    </Table>
  );
}

// Level 0: one row per machine, summed across every stored program_report row.
function MachineGroupRow({ row, filters, method, onOpenReasons, onOpenIdlePareto }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
        <TableCell>
          <IconButton size="small">
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.machine_name}</TableCell>
        <TableCell align="right">{secondsToHms(row.run_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.idle_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.stop_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.alarm_time)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_idle)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_run)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_idle1)}</TableCell>
        <TableCell align="right">{fmtCost(row.idle_cost)}</TableCell>
        <TableCell align="right">{fmtCost(row.extra_part_cost)}</TableCell>
        <TableCell align="right">{fmtCount(row.extra_part_count)}</TableCell>
        <TableCell align="right">{secondsToHms(row.extra_part_run_time)}</TableCell>
        <TableCell align="right">{row.actual ?? '—'}</TableCell>
        <TableCell align="right">{row.low_cycle ?? 0}</TableCell>
        <TableCell align="right">{row.normal_cycle ?? 0}</TableCell>
        <TableCell align="right">{row.high_cycle ?? 0}</TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => onOpenReasons && onOpenReasons(row.machine_name, filters?.startTimeMs, filters?.endTimeMs, undefined, undefined, row.run_time)}
            sx={{
              textTransform: 'none',
              fontSize: '11px',
              py: 0.25,
              px: 1,
              whiteSpace: 'nowrap',
              color: '#ff9800',
              borderColor: '#ff9800',
              '&:hover': {
                borderColor: '#e65100',
                backgroundColor: 'rgba(255, 152, 0, 0.08)',
              },
            }}
          >
            Reason
          </Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={18} sx={{ p: 0, borderBottom: expanded ? undefined : 'none' }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {expanded && (
              <JobGroupRows
                machine={row.machine_name}
                filters={filters}
                method={method}
                onOpenReasons={onOpenReasons}
                onOpenIdlePareto={onOpenIdlePareto}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ProgramComponentReportTab({ filters, method = '1', shifts = [] }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [reasonModalState, setReasonModalState] = useState({
    open: false,
    machine: '',
    sessionStartTime: null,
    sessionEndTime: null,
    componentName: '',
    machiningTime: null,
    runTime: null,
    fromDate: null,
    toDate: null,
  });

  const [paretoModalState, setParetoModalState] = useState({
    open: false,
    machine: '',
    componentName: '',
    machiningTime: null,
    data: [],
    loading: false,
    error: '',
  });

  const handleOpenReasons = (machine, startTime, endTime, componentName, machiningTime, runTime) => {
    setReasonModalState({
      open: true,
      machine,
      sessionStartTime: startTime,
      sessionEndTime: endTime,
      componentName: componentName || '',
      machiningTime: machiningTime ?? null,
      runTime: runTime ?? null,
      fromDate: filters?.startDateISO || null,
      toDate: filters?.endDateISO || null,
    });
  };

  const handleCloseReasons = () => {
    setReasonModalState((prev) => ({ ...prev, open: false }));
  };

  const handleOpenIdlePareto = async (machineName, componentName, machiningTime) => {
    setParetoModalState({
      open: true,
      machine: machineName,
      componentName: componentName || '',
      machiningTime: machiningTime ?? null,
      data: [],
      loading: true,
      error: '',
    });

    try {
      const shiftNo = Array.isArray(filters?.shift) ? filters.shift.join(',') : (filters?.shift || 'allshift');
      const fromTime = filters?.startDateISO || '';
      const toTime = filters?.endDateISO || '';

      const response = await getComponentReportIdleDetail(machineName, shiftNo, fromTime, toTime, { componentName, cycleTime: machiningTime });
      const list = response?.idleReasonWithPercentage || response?.data || (Array.isArray(response) ? response : []);
      setParetoModalState((prev) => ({
        ...prev,
        loading: false,
        data: Array.isArray(list) ? list : [],
      }));
    } catch (err) {
      console.error('Failed to fetch idle reason report for Pareto chart', err);
      setParetoModalState((prev) => ({
        ...prev,
        loading: false,
        error: 'Failed to load idle reason report data.',
      }));
    }
  };

  const handleCloseIdlePareto = () => {
    setParetoModalState((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (!filters) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        let startTimeMs = filters.startTimeMs;
        let endTimeMs = filters.endTimeMs;

        if (!startTimeMs || !endTimeMs) {
          const times = getStartAndEndTime(filters.startDateISO, filters.endDateISO, shifts);
          startTimeMs = times.startTimeMs;
          endTimeMs = times.endTimeMs;
        }

        const rawData = await getMachineProgramReportGroupDetail(
          filters.machines,
          startTimeMs,
          endTimeMs
        );

        if (!cancelled) {
          const programReportData = Array.isArray(rawData)
            ? rawData
            : Array.isArray(rawData?.data)
            ? rawData.data
            : Array.isArray(rawData?.result)
            ? rawData.result
            : [];

          setRows(programReportData);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch component report data', err);
          setError('Failed to load component report. Please try again.');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filters, method, shifts]);

  if (!filters) {
    return <StatusBox>Select filters and click Submit to load the component report.</StatusBox>;
  }

  if (loading) {
    return (
      <StatusBox>
        <CircularProgress size={28} />
      </StatusBox>
    );
  }

  if (error) {
    return <StatusBox>{error}</StatusBox>;
  }

  if (rows.length === 0) {
    return <StatusBox>No data for the selected filters.</StatusBox>;
  }

  return (
    <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, background: '#ffffff', overflow: 'hidden' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontWeight: 700, fontSize: '16px', color: TEXT_PRIMARY }}>
          Component Report — Machine-wise
        </Typography>
        <Typography sx={{ fontSize: '13px', color: TEXT_MUTED }}>
          Click a row to drill down: machine → job/program → session → individual cycle.
        </Typography>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#f9f9f7' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 700 }}>Machine</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Run Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Idle Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Stop Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Alarm Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 160 }}>Total Additional<br />Handling Time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 85 }}>Additional Run</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 110 }}>Extra Idle<br />(Net)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 100 }}>Idle Cost</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 110 }}>Extra Part Cost</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 110 }}>Additional<br />parts</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, minWidth: 165 }}>Hours lost to<br />excess cycle time</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actual</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Low</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Normal</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>High</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <MachineGroupRow
                key={row.machine_name}
                row={row}
                filters={filters}
                method={method}
                onOpenReasons={handleOpenReasons}
                onOpenIdlePareto={handleOpenIdlePareto}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ReasonDetailModal
        open={reasonModalState.open}
        onClose={handleCloseReasons}
        machine={reasonModalState.machine}
        sessionStartTime={reasonModalState.sessionStartTime}
        sessionEndTime={reasonModalState.sessionEndTime}
        componentName={reasonModalState.componentName}
        machiningTime={reasonModalState.machiningTime}
        runTime={reasonModalState.runTime}
        fromDate={reasonModalState.fromDate}
        toDate={reasonModalState.toDate}
      />

      <IdleReasonParetoModal
        open={paretoModalState.open}
        onClose={handleCloseIdlePareto}
        machine={paretoModalState.machine}
        loading={paretoModalState.loading}
        error={paretoModalState.error}
        data={paretoModalState.data}
      />
    </Box>
  );
}

function IdleReasonParetoModal({ open, onClose, machine, loading, error, data }) {
  const chartData = React.useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null;

    const parseToSeconds = (val) => {
      if (typeof val === 'number') return val;
      if (!val) return 0;
      if (typeof val === 'string' && val.includes(':')) {
        const parts = val.split(':').map(Number);
        if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
        if (parts.length === 2) return parts[0] * 60 + parts[1];
      }
      return parseFloat(val) || 0;
    };

    const aggregatedMap = {};
    data.forEach((item) => {
      const reason = item.name || item.idle_reason || item.reason || item.idle_reason_name || item.bucket || 'Uncategorized';
      const secs = parseToSeconds(item.total_idle_duration ?? item.idle_duration ?? item.duration ?? item.idle_time);
      aggregatedMap[reason] = (aggregatedMap[reason] || 0) + secs;
    });

    const sorted = Object.entries(aggregatedMap)
      .map(([reason, secs]) => ({ reason, secs }))
      .sort((a, b) => b.secs - a.secs);

    const categories = [];
    const barSeries = [];

    sorted.forEach((item) => {
      categories.push(item.reason);
      barSeries.push(item.secs);
    });

    const totalSecs = barSeries.reduce((acc, secs) => acc + secs, 0);

    let cum = 0;
    const lineSeries = barSeries.map((secs) => {
      cum += secs;
      return totalSecs > 0 ? Number(((cum / totalSecs) * 100).toFixed(1)) : 0;
    });

    return {
      categories,
      totalDuration: secondsToShortDuration(totalSecs),
      series: [
        {
          name: 'Idle Duration',
          type: 'column',
          data: barSeries,
        },
        {
          name: 'Cumulative %',
          type: 'line',
          data: lineSeries,
        },
      ],
      options: {
        chart: {
          type: 'line',
          toolbar: {
            show: true,
            tools: {
              pan: false,
            },
          },
          fontFamily: 'inherit',
        },
        colors: ['#2563eb', '#dc2626'],
        stroke: {
          width: [0, 3],
          curve: 'smooth',
        },
        plotOptions: {
          bar: {
            columnWidth: '45%',
            borderRadius: 4,
          },
        },
        xaxis: {
          categories,
          labels: {
            rotate: -45,
            style: { fontSize: '11px', fontWeight: 600 },
          },
        },
        yaxis: [
          {
            title: { text: 'Idle Duration', style: { color: '#2563eb', fontWeight: 600 } },
            labels: {
              formatter: (val) => secondsToShortDuration(val),
            },
          },
          {
            opposite: true,
            title: { text: 'Cumulative %', style: { color: '#dc2626', fontWeight: 600 } },
            min: 0,
            max: 100,
            labels: {
              formatter: (val) => `${val}%`,
            },
          },
        ],
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val, { seriesIndex }) => (seriesIndex === 0 ? secondsToShortDuration(val) : `${val}%`),
          },
        },
        legend: {
          position: 'top',
        },
      },
    };
  }, [data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Idle Reason Pareto Chart — {machine}
          </Typography>
          {chartData && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Total Idle Duration: {chartData.totalDuration}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress size={36} />
          </Box>
        ) : error ? (
          <Typography color="error" sx={{ p: 2 }}>{error}</Typography>
        ) : !chartData || chartData.categories.length === 0 ? (
          <Typography sx={{ p: 2, color: 'text.secondary', textAlign: 'center' }}>
            No idle reason data found for machine {machine}.
          </Typography>
        ) : (
          <Box sx={{ pt: 1 }}>
            <ReactApexChart options={chartData.options} series={chartData.series} type="line" height={400} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
